"use client";

import * as React from "react";
import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@workspace/ui/components/dialog";

import IconYoutubeNew from "@workspace/ui/icons/icon-youtube-new";
import {
  EventNames,
  SectionTypes,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";

const MDJ_QUOTE_ASSET_BASE =
  "https://cdn.inito.com/inito_website/home/assets/mdj-quote";

export type MdjQuoteSectionProps = {
  mobileQuote: string;
  desktopQuote: string;
  name: string;
  mobileRole: string;
  mobileSubs: string;
  desktopRole: string;
  mobileImage: string;
  desktopImage: string;
  videoSources: {
    webm: string;
    mp4: string;
  };
  desktopVideoSources?: {
    webm: string;
    mp4: string;
  };
};


function MobileMdjQuote({
  mobileQuote,
  name,
  mobileRole,
  mobileSubs,
  onWatchClick,
}: Pick<
  MdjQuoteSectionProps,
  "mobileQuote" | "name" | "mobileRole" | "mobileSubs"
> & { onWatchClick: () => void }) {
  return (
    <article className="relative mx-auto flex h-[700px] w-[430px] max-w-full flex-col items-start justify-end gap-5 overflow-hidden bg-[#1F130B] px-8 pb-14 pt-6 xl:hidden">
      <Image
        src="https://cdn.inito.com/inito_website/home/assets/mdj-quote/mdj_mobile_bg_.new.png"
        alt=""
        width={933}
        height={700}
        aria-hidden="true"
        className="pointer-events-none absolute -left-[358.895px] -top-[122.782px] h-full w-[216.874%] max-w-none object-contain"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(179deg,rgba(31,19,11,0)_44.15%,#1F130B_63.01%)]"
      />
      <button
        type="button"
        onClick={onWatchClick}
        id="mdj-quote-watch-overlay-mobile"
        data-testid="mdj-quote-watch-overlay-mobile"
        className="absolute inset-0 w-full cursor-pointer"
        aria-label="Watch Dr. Danielle Jones video"
      />

      <button
        type="button"
        onClick={onWatchClick}
        id="mdj-quote-watch-badge-mobile"
        data-testid="mdj-quote-watch-badge-mobile"
        className="absolute right-6 top-6 z-10 cursor-pointer"
        aria-label="Watch Dr. Danielle Jones video"
      >
        <Image
          src={`${MDJ_QUOTE_ASSET_BASE}/watch-badge-mobile.png`}
          alt="Watch"
          width={100}
          height={44}
          unoptimized
        />
      </button>

      <div className="relative z-10 flex w-full flex-col gap-7 text-base-1">
        <div className="flex flex-col gap-3">
          <p
            id="mdj-quote-name-mobile"
            data-testid="mdj-quote-name-mobile"
            className="title-24 text-base-2"
          >
            {name}
          </p>
          <div className="flex flex-nowrap items-center gap-[6px] opacity-60">
            <p
              id="mdj-quote-role-mobile"
              data-testid="mdj-quote-role-mobile"
              className="captitle-12 text-base-2"
            >
              {mobileRole}
            </p>
            <span className="captitle-12 text-base-2">&bull;</span>
            <div className="flex items-center gap-[1.69px] text-base-2">
              <IconYoutubeNew className="size-[18.57px]" />
              <p
                id="mdj-quote-subs-mobile"
                data-testid="mdj-quote-subs-mobile"
                className="captitle-12"
              >
                {mobileSubs}
              </p>
            </div>
          </div>
        </div>

        <p
          id="mdj-quote-text-mobile"
          data-testid="mdj-quote-text-mobile"
          className="font-highlight text-[20px] leading-[27px]"
        >
          {mobileQuote}
        </p>
      </div>
    </article>
  );
}

function DesktopMdjQuote({
  desktopQuote,
  name,
  mobileRole,
  mobileSubs,
  desktopImage,
  videoSources,
  desktopVideoSources,
  isVideoPlaying,
  onWatchClick,
  onVideoPlay,
}: Pick<
  MdjQuoteSectionProps,
  | "desktopQuote"
  | "name"
  | "mobileRole"
  | "mobileSubs"
  | "desktopImage"
  | "videoSources"
  | "desktopVideoSources"
> & {
  isVideoPlaying: boolean;
  onWatchClick: () => void;
  onVideoPlay: () => void;
}) {
  return (
    <article className="hidden rounded-[24px] bg-beige p-6 xl:block">
      <div className="grid grid-cols-[691px_minmax(0,1fr)] items-center gap-[17px]">
        <div className="flex h-full flex-col justify-center gap-8 p-12">
          <p
            id="mdj-quote-text-desktop"
            data-testid="mdj-quote-text-desktop"
            className="font-highlight text-[32px] leading-[43px] tracking-[-0.64px] text-primary"
          >
            {desktopQuote}
          </p>
          <div className="flex flex-col gap-2.5">
            <p
              id="mdj-quote-name-desktop"
              data-testid="mdj-quote-name-desktop"
              className="title-16 text-primary"
            >
              {name}
            </p>
            <div className="flex flex-nowrap items-center gap-[6px] text-secondary/70">
              <p
                id="mdj-quote-role-desktop"
                data-testid="mdj-quote-role-desktop"
                className="captitle-12"
              >
                {mobileRole}
              </p>
              <span className="captitle-12">&bull;</span>
              <div className="flex items-center gap-[1.69px]">
                <IconYoutubeNew className="size-[18.57px]" />
                <p
                  id="mdj-quote-subs-desktop"
                  data-testid="mdj-quote-subs-desktop"
                  className="captitle-12"
                >
                  {mobileSubs}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative min-h-[360px] overflow-hidden rounded-[20px]">
          {isVideoPlaying ? (
            <video
              poster={desktopImage}
              autoPlay
              controls
              playsInline
              className="absolute inset-0 h-full w-full object-cover"
              onPlay={onVideoPlay}
            >
              <source src={(desktopVideoSources ?? videoSources).webm} type="video/webm" />
              <source src={(desktopVideoSources ?? videoSources).mp4} type="video/mp4" />
            </video>
          ) : (
            <>
              <button
                type="button"
                onClick={onWatchClick}
                id="mdj-quote-watch-overlay-desktop"
                data-testid="mdj-quote-watch-overlay-desktop"
                className="absolute inset-0 block w-full cursor-pointer text-left"
                aria-label="Watch Dr. Danielle Jones video"
              >
                <Image
                  src={desktopImage}
                  alt="Dr. Danielle Jones portrait"
                  fill
                  sizes="(min-width: 1280px) 40vw, 0px"
                  className="object-cover"
                  priority={false}
                />
              </button>
              <div className="absolute inset-0 bg-black/5" />
              <button
                type="button"
                onClick={onWatchClick}
                id="mdj-quote-watch-badge-desktop"
                data-testid="mdj-quote-watch-badge-desktop"
                className="absolute left-4 top-4 z-10 cursor-pointer"
                aria-label="Watch Dr. Danielle Jones video"
              >
                <Image
                  src={`${MDJ_QUOTE_ASSET_BASE}/watch-badge.png`}
                  alt="Watch"
                  width={100}
                  height={44}
                  unoptimized
                />
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

export function MdjQuoteSection(props: MdjQuoteSectionProps) {
  const [isMobileVideoOpen, setIsMobileVideoOpen] = React.useState(false);
  const [isDesktopVideoPlaying, setIsDesktopVideoPlaying] =
    React.useState(false);
  const hasTrackedVideoPlay = React.useRef(false);

  const trackVideoPlay = React.useCallback(() => {
    if (hasTrackedVideoPlay.current) return;
    hasTrackedVideoPlay.current = true;
    sendEvent(
      EventNames.VIDEO_PLAYED,
      withSectionType(SectionTypes.RECOMMENDED_BY_CLINICIANS),
    );
  }, []);

  return (
    <>
      <section className="bg-base-1 px-0 xl:px-20 xl:py-15">
        <div className="mx-auto max-w-[1440px]">
          <MobileMdjQuote
            {...props}
            onWatchClick={() => setIsMobileVideoOpen(true)}
          />
          <DesktopMdjQuote
            {...props}
            isVideoPlaying={isDesktopVideoPlaying}
            onWatchClick={() => setIsDesktopVideoPlaying(true)}
            onVideoPlay={trackVideoPlay}
          />
        </div>
      </section>

      <Dialog open={isMobileVideoOpen} onOpenChange={setIsMobileVideoOpen}>
        <DialogContent
          className="max-w-[90vw] overflow-hidden border-0 bg-black p-0 xl:hidden"
          showCloseButton
          closeButtonClass="right-3 top-7 z-10 text-white"
        >
          <DialogTitle className="sr-only">
            Dr. Danielle Jones video
          </DialogTitle>
          <video
            poster={props.mobileImage}
            autoPlay
            controls
            playsInline
            className="aspect-[9/16] w-full"
            onPlay={trackVideoPlay}
          >
            <source src={props.videoSources.webm} type="video/webm" />
            <source src={props.videoSources.mp4} type="video/mp4" />
          </video>
        </DialogContent>
      </Dialog>
    </>
  );
}
